import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useCallback, useRef } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Type, Info } from 'lucide-react';
import { HeadlineOverlay } from '@/store/videoFeatures/types/headline-overlay';
import { VideoFeatures } from '@/store/videoFeatures';
import { HeadlineConfigTab } from './HeadlineConfigTab';
import { HeadlineCustomizationTab } from './HeadlineCustomizationTab';
import { HeadlineABTestTab } from './HeadlineABTestTab';
import { HeadlineMetricsTab } from './HeadlineMetricsTab';
import { defaultHeadlineOverlay } from '@/store/videoFeatures/types/headline-overlay';
import { toast } from 'sonner';
import { HeroCard } from '@/components/ui/HeroCard';
import { useUserHeadlineTests } from '@/hooks/useHeadlineTest';
import { useAuth } from '@/hooks/useAuth';
import { getHeadlineTestMetrics, HeadlineTestMetrics } from '@/services/headlineTestService';
import { format } from 'date-fns';
import { buildPresetRange } from '@/utils/period-presets';

interface HeadlineOverlayConfigProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
  onActiveTabChange?: (tab: string) => void;
}

export const HeadlineOverlayConfig: React.FC<HeadlineOverlayConfigProps> = ({
  features,
  setFeature,
  onActiveTabChange
}) => {
  useLanguage();
  const [activeTab, setActiveTab] = useState('config');
  const { user, token } = useAuth();

  // Estado das métricas do teste de headline controlado no pai — garante que a
  // rota só é chamada UMA VEZ por sessão quando a aba "Métricas" é aberta
  // (mesmo se o usuário alternar entre abas, o estado persiste aqui).
  const [metricsData, setMetricsData] = useState<HeadlineTestMetrics[] | null>(null);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [metricsStartDate, setMetricsStartDate] = useState<Date>(
    () => buildPresetRange('last30Days', new Date()).from
  );
  const [metricsEndDate, setMetricsEndDate] = useState<Date>(
    () => buildPresetRange('last30Days', new Date()).to
  );
  // Ref com flag "já carregou uma vez?" — evita refetch automático na
  // remontagem de filhos (TabsContent do Radix desmonta quando inativo).
  const metricsLoadedRef = useRef(false);

  const fetchHeadlineMetrics = useCallback(
    async (headlineTestId: string, opts?: { force?: boolean }) => {
      if (!headlineTestId || !token) return;
      if (!opts?.force && metricsLoadedRef.current) return;

      setIsLoadingMetrics(true);
      try {
        const data = await getHeadlineTestMetrics(
          headlineTestId,
          format(metricsStartDate, 'yyyy-MM-dd'),
          format(metricsEndDate, 'yyyy-MM-dd'),
          token,
        );
        setMetricsData(data);
        metricsLoadedRef.current = true;
      } catch (error: any) {
        console.error('Erro ao carregar métricas do teste de headline:', error);
        toast.error(error?.response?.data?.message || translateInterfaceText("Erro ao carregar métricas"));
      } finally {
        setIsLoadingMetrics(false);
      }
    },
    [token, metricsStartDate, metricsEndDate],
  );

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    onActiveTabChange?.(tab);

    if (tab === 'metrics') {
      // Após finalizar/excluir o teste, status='finished' mas o testId pode
      // ainda estar no estado — nesse caso o teste não existe no backend e
      // não devemos chamar a rota de métricas.
      const config = headlineOverlay?.abTestConfig;
      if (config?.headlineTestId && config.status !== 'finished') {
        fetchHeadlineMetrics(config.headlineTestId);
      }
    }
  };

  // Buscar testes de headline do usuário quando o componente montar
  const { data: userHeadlineTests, isLoading: isLoadingTests } = useUserHeadlineTests(user?.id_user || user?.id);

  // Verificar se o vídeo atual tem um teste ativo e popular os dados
  React.useEffect(() => {
    if (!userHeadlineTests || !features.videoId) return;

    // Procurar um teste que pertença ao vídeo atual
    const currentVideoTest = userHeadlineTests.find(
      (test: any) => test.id_video === features.videoId
    );

    if (currentVideoTest) {
      // O mapper do backend (user-headline-test.mapper.js) retorna o id do
      // teste na chave `id` (não `id_headline_test`). Aceita ambos como
      // fallback por compatibilidade.
      const testId = currentVideoTest.id || currentVideoTest.id_headline_test;

      // Verificar se já está populado (para evitar loop infinito)
      if (headlineOverlay.abTestConfig?.headlineTestId !== testId) {
        updateHeadlineOverlay('abTestConfig', {
          ...headlineOverlay.abTestConfig,
          enabled: true,
          status: 'active' as const,
          headlineTestId: testId,
          startDate: currentVideoTest.createdAt || currentVideoTest.created_at || new Date().toISOString(),
        });

        toast.success(translateInterfaceText("Teste de headline carregado!"));
      }
    }
  }, [userHeadlineTests, features.videoId]);

  // Ensure headline is initialized with default values with validation
  const headlineOverlay = React.useMemo(() => {
    try {
      const baseHeadline = features.headline || defaultHeadlineOverlay;
      
      // Validação profunda das propriedades
      const validatedHeadline = {
        ...defaultHeadlineOverlay,
        ...baseHeadline,
        // Garantir que objetos aninhados existem
        headline: {
          ...defaultHeadlineOverlay.headline,
          ...(baseHeadline.headline || {})
        },
        subheadline: {
          ...defaultHeadlineOverlay.subheadline,
          ...(baseHeadline.subheadline || {})
        },
        timing: {
          ...defaultHeadlineOverlay.timing,
          ...(baseHeadline.timing && typeof baseHeadline.timing === 'object' && !Array.isArray(baseHeadline.timing) ? baseHeadline.timing : {})
        },
        position: Object.assign(
          {},
          defaultHeadlineOverlay.position,
          baseHeadline.position && typeof baseHeadline.position === 'object' && !Array.isArray(baseHeadline.position) ? baseHeadline.position : {}
        ),
        background: {
          ...defaultHeadlineOverlay.background,
          ...(baseHeadline.background && typeof baseHeadline.background === 'object' ? baseHeadline.background : {})
        },
        animation: {
          ...defaultHeadlineOverlay.animation,
          ...(baseHeadline.animation && typeof baseHeadline.animation === 'object' ? baseHeadline.animation : {})
        },
        htmlMode: {
          ...defaultHeadlineOverlay.htmlMode,
          ...(baseHeadline.htmlMode && typeof baseHeadline.htmlMode === 'object' ? baseHeadline.htmlMode : {})
        },
        imageMode: {
          ...defaultHeadlineOverlay.imageMode,
          ...(baseHeadline.imageMode && typeof baseHeadline.imageMode === 'object' ? baseHeadline.imageMode : {})
        },
        abTestConfig: {
          ...defaultHeadlineOverlay.abTestConfig,
          ...(baseHeadline.abTestConfig && typeof baseHeadline.abTestConfig === 'object' ? baseHeadline.abTestConfig : {})
        }
      };
      
      // Migração: converter mainImage antiga para desktopImage
      if ((validatedHeadline.imageMode as any).mainImage && !validatedHeadline.imageMode.desktopImage) {
        validatedHeadline.imageMode.desktopImage = (validatedHeadline.imageMode as any).mainImage;
        delete (validatedHeadline.imageMode as any).mainImage;
      }

      return validatedHeadline;
    } catch (error) {
      return defaultHeadlineOverlay;
    }
  }, [features.headline]);

  // Migrar métricas antigas para o novo formato
  React.useEffect(() => {
    const variations = headlineOverlay.abTestConfig.variations;
    
    if (variations.length > 0) {
      const firstVariation = variations[0];
      
      // Verificar se precisa migrar (estrutura antiga sem uniqueViews)
      if (firstVariation.metrics && firstVariation.metrics.uniqueViews === undefined) {
        const migratedVariations = variations.map(variation => ({
          ...variation,
          metrics: {
            views: variation.metrics?.views || 0,
            uniqueViews: Math.round((variation.metrics?.views || 0) * 0.85),
            plays: variation.metrics?.clicks || 0,
            uniquePlays: Math.round((variation.metrics?.clicks || 0) * 0.8),
            playRate: variation.metrics?.ctr || 0,
            engagement: 0,
            pitchRetention: 0,
            clicks: variation.metrics?.clicks || 0,
            ctr: variation.metrics?.ctr || 0,
            avgDisplayTime: variation.metrics?.avgDisplayTime || 0,
            conversions: 0,
            conversionRate: 0,
            revenue: 0,
            platform: 'Meta'
          }
        }));
        
        updateHeadlineOverlay('abTestConfig', {
          ...headlineOverlay.abTestConfig,
          variations: migratedVariations
        });
        
        toast.success(translateInterfaceText("Métricas atualizadas para novo formato!"));
      }
    }
  }, []); // Executar apenas na montagem

  const updateHeadlineOverlay = (key: keyof HeadlineOverlay, value: any) => {
    try {
      const updatedHeadline = {
        ...headlineOverlay,
        [key]: value
      };

      setFeature('headline', updatedHeadline);
    } catch (error) {
      // Error handling
    }
  };

  const updateHeadlineOverlayBatch = (updates: Partial<HeadlineOverlay>) => {
    try {
      setFeature('headline', { ...headlineOverlay, ...updates });
    } catch (error) {
      // Error handling
    }
  };

  try {
    return (
      <div className="flex flex-col h-full min-h-0">
        {/* Conteúdo principal com scroll */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {/* Switch Mestre */}
          <HeroCard
            icon={<Type className="w-full h-full" strokeWidth={2} />}
            title={translateInterfaceText("Headline")}
            description={translateInterfaceText("Ao ativar, a headline será exibida diretamente sobre o vídeo conforme suas configurações.")}
            isActive={headlineOverlay.enabled}
            onToggle={(checked) => updateHeadlineOverlay('enabled', checked)}
          />

          {/* Main Configuration - Tabs com scroll */}
          {headlineOverlay.enabled && (
            <div className="flex flex-col flex-1 min-h-0 mt-6">
              <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full flex flex-col flex-1 min-h-0 overflow-hidden">
                <TabsList className="w-full bg-[#F7F9FC] rounded-xl p-1 mb-4">
                  <TabsTrigger
                    value="config"
                    className="flex-1 h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#1D4FFF] data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:text-[#64748B] data-[state=inactive]:bg-transparent"
                  >
                    {translateInterfaceText("Configurações")}</TabsTrigger>
                  <TabsTrigger
                    value="abtest"
                    className="flex-1 h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#1D4FFF] data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:text-[#64748B] data-[state=inactive]:bg-transparent"
                  >
                    {translateInterfaceText("Teste A/B")}</TabsTrigger>
                  <TabsTrigger
                    value="metrics"
                    className="flex-1 h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#1D4FFF] data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:text-[#64748B] data-[state=inactive]:bg-transparent"
                  >
                    {translateInterfaceText("Métricas")}</TabsTrigger>
                </TabsList>

                {/* Alerta informativo - aparece sempre que o recurso está ativo */}
                <div className="flex items-center gap-2 p-3 bg-[#F0F7FF] border border-[#D4E5FF] rounded-lg mb-4">
                  <Info className="h-4 w-4 text-[#1F6BFF] flex-shrink-0" />
                  <span className="text-xs text-[#1C1C1E]">
                    {translateInterfaceText("Para que as alterações da headline funcionem, você precisa copiar o código do vídeo depois de salvar e colar no seu site.")}</span>
                </div>

                <TabsContent value="config" className="h-full flex-1 overflow-hidden px-0 py-0">
                  <HeadlineConfigTab
                    headlineOverlay={headlineOverlay}
                    updateHeadlineOverlay={updateHeadlineOverlay}
                    updateHeadlineOverlayBatch={updateHeadlineOverlayBatch}
                  />
                </TabsContent>

                <TabsContent value="abtest" className="flex-1 min-h-0 flex flex-col px-2 py-2 mt-0 overflow-hidden h-full">
                  <HeadlineABTestTab
                    headlineOverlay={headlineOverlay}
                    updateHeadlineOverlay={updateHeadlineOverlay}
                    canUseABTest={true}
                    videoId={features.videoId}
                  />
                </TabsContent>

                <TabsContent value="metrics" className="h-full flex-1 overflow-hidden px-0 py-0">
                  <HeadlineMetricsTab
                    headlineOverlay={headlineOverlay}
                    updateHeadlineOverlay={updateHeadlineOverlay}
                    headlineTestId={headlineOverlay.abTestConfig?.headlineTestId}
                    metricsData={metricsData}
                    isLoadingMetrics={isLoadingMetrics}
                    startDate={metricsStartDate}
                    endDate={metricsEndDate}
                    onStartDateChange={setMetricsStartDate}
                    onEndDateChange={setMetricsEndDate}
                    onRefreshMetrics={() => {
                      const config = headlineOverlay.abTestConfig;
                      if (config?.headlineTestId && config.status !== 'finished') {
                        fetchHeadlineMetrics(config.headlineTestId, { force: true });
                      }
                    }}
                  />
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </div>
    );
  } catch (error) {
    throw error;
  }
};
